import React, { useState, useRef, useCallback, useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import ImpastoPastelThumb from './ImpastoPastelThumb';

/**
 * Feature card: click once to flip to details; click again (or Enter Now) to enter.
 * Premium Artmosphere museum styling.
 */
const FlipCard = ({
  featureKey,
  title,
  short,
  long,
  bullets = [],
  imageUrl,
  impastoVariant,
  isFlipped,
  onFlip,
  onEnter,
  className = '',
}) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobileLayout(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (isFlipped) {
      onEnter?.(featureKey);
      return;
    }
    onFlip?.(featureKey);
  };

  const handleEnterClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onEnter?.(featureKey);
  };

  const handleMouseMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    const maxTilt = 14;
    setTilt({ x: -y * maxTilt, y: x * maxTilt });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const hoverLift = isHovered && !isMobileLayout ? ' scale(1.04) translateY(-8px)' : '';
  const tiltStyle = {
    transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)${hoverLift}`,
    transition: tilt.x === 0 && tilt.y === 0 ? 'transform 0.3s ease' : 'transform 0.1s ease-out',
  };

  const flipTransform = isFlipped
    ? isMobileLayout
      ? 'rotateY(180deg)'
      : 'rotateY(180deg) scale(1.08) translateY(-12px)'
    : 'rotateY(0deg) scale(1) translateY(0)';

  const frontHover = isHovered ? ' flip-card-front--hover' : '';
  const backState = isHovered ? ' flip-card-back--hover' : isFlipped ? ' flip-card-back--flipped' : '';

  return (
    <div
      ref={cardRef}
      className={`flip-card-wrapper cursor-pointer select-none overflow-visible relative ${isFlipped ? 'z-[100]' : 'z-0'} ${className}`}
      style={{ perspective: '1000px' }}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="flip-card-tilt w-full h-full min-h-[200px] origin-center"
        style={{ ...tiltStyle, transformStyle: 'preserve-3d' }}
      >
        <div
          className="flip-card-inner relative w-full h-full min-h-[200px] overflow-visible origin-center"
          style={{
            transformStyle: 'preserve-3d',
            transform: flipTransform,
            transition: 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <div
            className={`flip-card-front flip-card-front-premium absolute inset-0 w-full h-full rounded-2xl overflow-hidden${frontHover}`}
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
            }}
          >
            <div className="absolute inset-0 w-full h-full">
              {impastoVariant ? (
                <ImpastoPastelThumb variant={impastoVariant} />
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#fff9ef] to-[#f5e8d4]/80" />
                  {imageUrl && (
                    <LazyLoadImage
                      src={imageUrl}
                      alt=""
                      effect="blur"
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                      wrapperClassName="absolute inset-0 w-full h-full"
                    />
                  )}
                </>
              )}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent rounded-2xl flex flex-col justify-end">
              <h3 className="font-bold text-white text-xs mb-0.5 line-clamp-2 leading-tight drop-shadow-md px-3 pb-1.5 pt-6">{title}</h3>
              <p className="text-white/95 text-[11px] line-clamp-2 leading-snug drop-shadow px-3 pb-2.5">{short}</p>
            </div>
          </div>

          <div
            className={`flip-card-back flip-card-back-premium absolute inset-0 w-full h-full rounded-2xl overflow-hidden flex flex-col${backState}`}
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              fontSize: '12px',
            }}
          >
            <div className="flex-1 min-h-0 p-2.5 flex flex-col overflow-hidden">
              <div className="flip-card-back-accent flex-shrink-0 pt-0.5 pb-1 border-b">
                <h3 className="flip-card-back-title flip-card-back-title-premium font-bold leading-tight">{title}</h3>
              </div>
              <p className="flex-shrink-0 font-medium text-[#6b5a48] mt-1 mb-0.5">What you&apos;ll see inside:</p>
              <ul className="list-none flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-0.5 pr-0.5 leading-snug text-[#5c4428]">
                {(bullets || []).slice(0, 3).map((b, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="flip-card-back-bullet flex-shrink-0 rounded-full mt-1" style={{ width: '3px', height: '3px', minWidth: '3px', minHeight: '3px' }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flip-card-back-footer flex-shrink-0 p-2 pt-1 border-t rounded-b-2xl">
              <button
                type="button"
                onClick={handleEnterClick}
                className="art-btn-primary w-full rounded-lg py-1 px-2.5 font-bold focus:outline-none focus:ring-2 focus:ring-[#f5cb5c]/50 focus:ring-offset-1 transition-all active:scale-[0.98]"
                style={{ fontSize: '12px' }}
              >
                Enter Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlipCard;
